import React, { useEffect, useRef, useState } from 'react';
import Modal from '../components/Modal';
import { fetchOmsRequisitionByOrderableItemId, logEvent } from '../util/apiUtils';
import workflowUtil from '../util/workflowUtil';
import RequisitionWorkflowStep from './RequisitionWorkflowStep';

/**
 * Displays and controls a Modal that can be used to save a new requisition to an existing Gear Order. The Modal
 * contents will update to walk through the four main requisition steps (Panels, Billing Options, Diagnosis Codes, and
 * Sample Collection).
 *
 * @param recommendedProduct Details of the GearRecommendedProduct to add
 * @param onCloseModal A method to call when the modal should close. Updated GearOrder and OmsRequisition details are
 *                     passed to this method if the product has been saved.
 * @param userInfo The logged in user
 * @param gearOrder The original Gear Order
 */
const AddToOrderModal = ({ recommendedProduct, onCloseModal, userInfo, gearOrder }) => {
	/* OMS Requisitiion structure of the product being added */
	const [omsRequisition, setOmsRequisition] = useState(null);
	/* Updated order details that have not been pushed to the parent yet */
	const [updatedGearOrder, setUpdatedGearOrder] = useState(null);
	const [loading, setLoading] = useState(false);
	const [activeGearRequisitionId, setActiveGearRequisitionId] = useState(null);
	const [currentRequisitionStep, setCurrentRequisitionStep] = useState('panel');
	const [error, setError] = useState(null);
	const scrollContainerRef = useRef(null);

	// Fetch the OMS Requisition structure
	const omsOrderableItemId = recommendedProduct?.omsOrderableItemId;
	const gsdlNo = gearOrder?.practitioner?.gsdlNo;
	useEffect(() => {
		if (omsOrderableItemId) {
			setOmsRequisition(null);
			setError(null);
			setLoading(true);

			fetchOmsRequisitionByOrderableItemId(omsOrderableItemId, gsdlNo)
				.then((json) => {
					setOmsRequisition(json);
				})
				.catch((e) => {
					showError(true);
				})
				.finally(() => setLoading(false));
		}
	}, [omsOrderableItemId, gsdlNo]);

	/**
	 * This method is called by <Modal> when clicking the close 'x' or clicking out of the modal.
	 * Prevent the Modal close when the loading flag is set (fetching or saving data).
	 * When the Modal can be closed, call handleModalClose, which will push appropriate data to the parent.
	 */
	const handleModalCloseRequest = (event) => {
		if (loading) {
			event?.preventDefault && event.preventDefault();
			return false;
		} else {
			handleModalClose();
			return true;
		}
	};

	/**
	 * Send data back to the parent when the Modal should close.
	 *
	 * If we have an updatedGearOrder it means a new product was saved to the order. Sending that to the parent will
	 * update the parent's state and display the new product details.
	 *
	 * It is important to call the parent method even when we don't have an updated order because that triggers the
	 * parent to dispose of this component.
	 */
	const handleModalClose = () => {
		let updateOrderRequest = null;
		if (updatedGearOrder) {
			updateOrderRequest = { gearOrder: updatedGearOrder, omsRequisition: omsRequisition };
		}
		onCloseModal(updateOrderRequest);
	};

	/**
	 * RequisitionWorkflowStep components call this method when they have saved data and want to broadcast the order
	 * updates and navigate to the next step.
	 *
	 * Determine the next step (next or previous based on input parameters) and display that step.
	 *
	 * If there is no next step it means we've either gotten to the end of the steps or the process was canceled.
	 * In that case, call the handleModalClose method to inform the parent (and push data if anything has been saved).
	 *
	 * @param gearOrder The updated Gear Order details
	 * @param currentStepKey The workflowMetaData key denoting which step called this method
	 * @param direction 'Next' or 'Back' to signal which direction to move
	 * @param gearReqId The ID of the Gear Req being saved or updated
	 */
	const updateRequisitionStep = (gearOrder, currentStepKey, direction, gearReqId) => {
		let nextStepKey = findRequisitionStep(gearOrder, currentStepKey, direction, gearReqId);

		// Log when a step is saved
		if (direction === 'Next' && currentStepKey && gearReqId) {
				const event = `RECOMMENDED_PRODUCTS_SAVE_STEP_${currentStepKey}`.toUpperCase();
				logEvent(
					`{} - Recommended Products Modal ${currentStepKey} step saved. gearOrderId={}, gearReqId={}, omsOrderableItemId={}`,
					event,
					gearOrder.id,
					gearReqId,
					omsOrderableItemId
				);
		}

		setCurrentRequisitionStep(nextStepKey);

		if (!nextStepKey) {
			handleModalClose();
		}
	};

	/**
	 * Display and scroll to an error message
	 */
	const showError = (errorMessage) => {
		// Ignore the incoming message and show a generic message
		if (errorMessage) {
			errorMessage = (
				<span>
					An unexpected error occurred. Please try again or call us at 800-522-4762 or contact{' '}
					<a
						href={`${process.env.REACT_APP_GDX_WEB_URL}/contact/providers#contact-us`}
						className="alert-link"
						target="_blank"
						rel="noopener noreferrer"
					>
						Customer Support
					</a>{' '}
					for help.
				</span>
			);
		}
		setError(errorMessage);

		if (errorMessage && scrollContainerRef?.current) {
			scrollContainerRef.current.scrollTo(0, 0);
		}
	};

	if (!recommendedProduct) {
		return null;
	}

	return (
		<Modal
			id={recommendedProduct?.omsOrderableItemId + '-recommended-product-modal'}
			modalLarge={true}
			title={'Add to Order' + (recommendedProduct?.title ? ` - ${recommendedProduct.title}` : '')}
			modalHidden={() => {} /* Not used */}
			displayProps={{ modalBody: { style: { padding: '0' } } }}
			doConfirm={handleModalCloseRequest}
			hideCloseX={loading}
		>
			{!omsRequisition && (
				<>
					{loading && (
						<div style={{ height: '200px' }}>
							<div className="loading-shield-opaque" style={{ position: 'absolute' }}>
								<i className="fa fa-spinner fa-spin fa-fw loading-icon" style={{ top: '50%' }}></i>
								<h2 style={{ position: 'relative', left: '1.5em' }}>Loading Product Details</h2>
							</div>
						</div>
					)}
					{error && (
						<div className="alert alert-danger" role="alert" style={{ margin: '30px 20px' }}>
							{error}
						</div>
					)}
				</>
			)}
			{omsRequisition && (
				<div ref={scrollContainerRef} style={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'clip' }}>
					<div style={{ padding: '0 15px' }}>
						{loading && (
							<div>
								<div className="loading-shield" style={{ position: 'absolute' }}>
									<i className="fa fa-spinner fa-spin fa-fw loading-icon"></i>
								</div>
							</div>
						)}

						{error && (
							<div className="alert alert-danger" role="alert">
								{error}
							</div>
						)}

						<RequisitionWorkflowStep
							workflowStepKey={currentRequisitionStep}
							userInfo={userInfo}
							gearOrder={updatedGearOrder || gearOrder}
							omsRequisition={omsRequisition}
							activeGearRequisitionId={activeGearRequisitionId}
							showError={showError}
							setLoading={setLoading}
							setGearOrder={setUpdatedGearOrder}
							setActiveGearRequisitionId={setActiveGearRequisitionId}
							updateStep={updateRequisitionStep}
							// The following methods are expected by the workflow steps but we don't need them for this feature
							doConfirm={() => true}
							setDirty={() => {} /* Not used */}
							setOmsRequisition={() => {} /* Not used */}
							setAddNewReq={() => {} /* Not used */}
						/>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default AddToOrderModal;

/**
 * Finds the previous or next workflow step by key and direction and returns the new step's workflowMetaData key
 *
 * @param gearOrder The gear order containing up-to-date workflow details
 * @param stepKey The key of the current step
 * @param direction The direction to move ('Next' or 'Back')
 * @param gearReqId the gear requisition id being saved or updated
 */
const findRequisitionStep = (gearOrder, currentStepKey, direction, gearReqId) => {
	// Filter the gearOrder workflow steps to only include the step's relevant to the given gearReqId
	const requisitionWorkflowSteps = gearOrder?.workflow?.steps?.filter(
		(workflowStep) => workflowStep?.gearReqId === gearReqId
	);
	const currentStepIndex = requisitionWorkflowSteps.findIndex(
		(workflowStep) => workflowStep?.id === workflowUtil.getWorkflowMetaDataByKey(currentStepKey)?.id
	);

	let nextWorkflowStep;
	if (!currentStepIndex && currentStepIndex !== 0) {
		nextWorkflowStep = null;
	} else if (direction === 'Next') {
		nextWorkflowStep = requisitionWorkflowSteps?.[currentStepIndex + 1];
	} else if (direction === 'Back') {
		nextWorkflowStep = requisitionWorkflowSteps?.[currentStepIndex - 1];
	}

	if (!nextWorkflowStep?.id) {
		return null;
	}

	const workflowMetaDataKey = Object.keys(workflowUtil.workflowMetaData).find(
		(metaDataKey) => workflowUtil.workflowMetaData[metaDataKey].id === nextWorkflowStep?.id
	);
	return workflowMetaDataKey;
};
